import { useState } from 'react';
import { Button } from '../components/Button/Button';
import { blobRawUrl } from './moderatorApi';
import type { AuditEntry, BlobEntry, ReportEntry } from './moderatorApi';
import {
  useDecideSubmission,
  useSubmissionAudit,
  useSubmissionBlobs,
  useSubmissionReports,
} from './useModerator';

interface Props {
  submissionId: string;
}

/**
 * Right-hand pane of the moderator dashboard. Shows everything a
 * moderator needs to make a call:
 *
 * - The audit chain (every CSAM scan, general moderation call, face
 *   blur run, and any prior overrides)
 * - The blobs themselves (with thumbnails for images, audio players
 *   for audio)
 * - User reports queued against the submission
 * - Approve / Reject buttons that override the pipeline outcome
 */
export function SubmissionDetail({ submissionId }: Props): JSX.Element {
  const audit = useSubmissionAudit(submissionId);
  const blobs = useSubmissionBlobs(submissionId);
  const reports = useSubmissionReports(submissionId);
  const decide = useDecideSubmission();
  const [reasonText, setReasonText] = useState('');

  const onApprove = (): void => {
    decide.mutate(
      {
        submissionId,
        payload: {
          decision: 'approve',
          ...(reasonText.trim() ? { reason: reasonText.trim() } : {}),
        },
      },
      { onSuccess: () => setReasonText('') },
    );
  };
  const onReject = (): void => {
    decide.mutate(
      {
        submissionId,
        payload: {
          decision: 'reject',
          ...(reasonText.trim() ? { reason: reasonText.trim() } : {}),
        },
      },
      { onSuccess: () => setReasonText('') },
    );
  };

  return (
    <div className="bl-mod-detail">
      <h2 className="bl-mod-detail__title">Submission</h2>
      <p className="bl-mod-detail__id">
        ID: <code>{submissionId}</code>
      </p>

      <DecisionPanel
        reasonText={reasonText}
        onReasonChange={setReasonText}
        onApprove={onApprove}
        onReject={onReject}
        pending={decide.isPending}
        error={decide.error?.message ?? null}
      />

      <BlobsPanel
        loading={blobs.isPending}
        error={blobs.error?.message ?? null}
        rows={blobs.data ?? []}
      />

      <AuditPanel
        loading={audit.isPending}
        error={audit.error?.message ?? null}
        rows={audit.data ?? []}
      />

      <ReportsPanel
        loading={reports.isPending}
        error={reports.error?.message ?? null}
        rows={reports.data ?? []}
      />
    </div>
  );
}

interface DecisionPanelProps {
  reasonText: string;
  onReasonChange: (s: string) => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
  error: string | null;
}

function DecisionPanel({
  reasonText,
  onReasonChange,
  onApprove,
  onReject,
  pending,
  error,
}: DecisionPanelProps): JSX.Element {
  return (
    <section
      className="bl-mod-detail__section bl-mod-detail__decision"
      aria-label="Moderator decision"
    >
      <h3>Decision</h3>
      <label className="bl-mod-detail__reason">
        <span>Reason (optional — visible to the submitter on reject)</span>
        <textarea
          value={reasonText}
          onChange={(e) => onReasonChange(e.target.value)}
          rows={2}
          placeholder="Short explanation, kid-friendly"
          maxLength={500}
        />
      </label>
      <div className="bl-mod-detail__actions">
        <Button onClick={onApprove} disabled={pending}>
          {pending ? 'Working…' : 'Approve'}
        </Button>
        <Button onClick={onReject} disabled={pending} variant="secondary">
          {pending ? 'Working…' : 'Reject'}
        </Button>
      </div>
      {error && (
        <p className="bl-mod-detail__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

interface BlobsPanelProps {
  loading: boolean;
  error: string | null;
  rows: BlobEntry[];
}

function BlobsPanel({ loading, error, rows }: BlobsPanelProps): JSX.Element {
  return (
    <section className="bl-mod-detail__section" aria-label="Submission blobs">
      <h3>Sample blobs</h3>
      {loading && <p>Loading blobs…</p>}
      {error && (
        <p className="bl-mod-detail__error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p className="bl-mod-detail__empty">No blobs were uploaded.</p>
      )}
      {rows.length > 0 && (
        <ul className="bl-mod-detail__blobs">
          {rows.map((b) => (
            <li key={b.id} className="bl-mod-detail__blob">
              <BlobPreview blob={b} />
              <div className="bl-mod-detail__blob-meta">
                <strong>{b.class_label}</strong>
                <span>
                  {b.kind} · {b.content_type} · {Math.round(b.size_bytes / 1024)} KB
                </span>
                <code title="sha256 of original bytes">{b.sha256.slice(0, 12)}…</code>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BlobPreview({ blob }: { blob: BlobEntry }): JSX.Element {
  const url = blobRawUrl(blob.id);
  if (blob.kind === 'image') {
    return (
      <img
        src={url}
        alt={`Sample for class "${blob.class_label}"`}
        className="bl-mod-detail__thumb"
        loading="lazy"
      />
    );
  }
  return (
    <audio controls preload="none" className="bl-mod-detail__audio" src={url}>
      <track kind="captions" />
    </audio>
  );
}

interface AuditPanelProps {
  loading: boolean;
  error: string | null;
  rows: AuditEntry[];
}

function AuditPanel({ loading, error, rows }: AuditPanelProps): JSX.Element {
  return (
    <section className="bl-mod-detail__section" aria-label="Audit chain">
      <h3>Audit chain</h3>
      {loading && <p>Loading audit…</p>}
      {error && (
        <p className="bl-mod-detail__error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p className="bl-mod-detail__empty">No audit entries yet.</p>
      )}
      {rows.length > 0 && (
        <ol className="bl-mod-detail__audit">
          {rows.map((r) => (
            <li key={r.id} className="bl-mod-detail__audit-row">
              <span className={`bl-mod__decision bl-mod__decision--${r.decision}`}>
                {r.decision}
              </span>
              <span className="bl-mod-detail__audit-stage">{r.stage}</span>
              <span className="bl-mod-detail__audit-provider">{r.provider}</span>
              <span className="bl-mod-detail__audit-reason">{r.reason}</span>
              <time className="bl-mod-detail__audit-time" dateTime={r.created_at}>
                {new Date(r.created_at).toLocaleTimeString()}
              </time>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

interface ReportsPanelProps {
  loading: boolean;
  error: string | null;
  rows: ReportEntry[];
}

function ReportsPanel({ loading, error, rows }: ReportsPanelProps): JSX.Element {
  return (
    <section className="bl-mod-detail__section" aria-label="User reports">
      <h3>Reports</h3>
      {loading && <p>Loading reports…</p>}
      {error && (
        <p className="bl-mod-detail__error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p className="bl-mod-detail__empty">No reports filed.</p>
      )}
      {rows.length > 0 && (
        <ul className="bl-mod-detail__reports">
          {rows.map((r) => (
            <li key={r.id} className="bl-mod-detail__report">
              <strong>{r.reason}</strong>
              {r.detail && <p>{r.detail}</p>}
              <time dateTime={r.created_at}>{new Date(r.created_at).toLocaleString()}</time>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
